<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import TaskAISuggestions from './TaskAISuggestions.vue';
import TaskCard from './TaskCard.vue';
import TaskEmptyState from './TaskEmptyState.vue';
import TaskForm from './TaskForm.vue';
import { AI_SUGGESTION_COUNT } from './taskAISuggestions.js';
import { TASK_FILTERS, matchesFilter, taskSort } from './taskHelpers.js';
import { useTasksStore } from './useTasksStore.js';

const props = defineProps({
  embedded: {
    type: Boolean,
    default: false,
  },
  contactId: {
    type: [String, Number],
    default: null,
  },
  projectId: {
    type: [String, Number],
    default: null,
  },
  limit: {
    type: Number,
    default: null,
  },
});

const { t, locale } = useI18n();
const tasksStore = useTasksStore();
const { state } = tasksStore;
const activeFilter = ref('today');
const activeMode = ref('tasks');
const taskFormRef = ref(null);
const priorityGroups = ['high', 'medium', 'low'];

const filterLabels = computed(() => ({
  today: t('TASKS.FILTERS.TODAY'),
  overdue: t('TASKS.FILTERS.OVERDUE'),
  upcoming: t('TASKS.FILTERS.UPCOMING'),
  done: t('TASKS.FILTERS.DONE'),
  all: t('TASKS.FILTERS.ALL'),
}));

const priorityLabels = computed(() => ({
  high: t('TASKS.PRIORITY.HIGH'),
  medium: t('TASKS.PRIORITY.MEDIUM'),
  low: t('TASKS.PRIORITY.LOW'),
}));

const headerTitle = computed(() => {
  if (activeMode.value === 'ai') {
    return t('TASKS.AI_SUGGESTIONS.TITLE');
  }

  if (activeFilter.value === 'today') {
    return filterLabels.value.today;
  }

  return filterLabels.value[activeFilter.value] || filterLabels.value.today;
});

const todayLabel = computed(() =>
  new Intl.DateTimeFormat(locale.value, {
    day: 'numeric',
    month: 'long',
    weekday: 'long',
  }).format(new Date())
);

const priorityGroupLabel = priority =>
  `${t('TASKS.FORM.PRIORITY')} ${priorityLabels.value[priority].toLowerCase()}`;

const scopedTasks = computed(() => {
  let tasks = state.tasks;
  if (props.contactId) {
    tasks = tasks.filter(
      task => String(task.linkedContactId) === String(props.contactId)
    );
  }
  if (props.projectId) {
    tasks = tasks.filter(
      task => String(task.linkedProjectId) === String(props.projectId)
    );
  }
  return tasks;
});

const filteredTasks = computed(() => {
  const sorted = scopedTasks.value
    .filter(task => matchesFilter(task, activeFilter.value))
    .slice()
    .sort((a, b) => taskSort(a, b));
  return props.limit ? sorted.slice(0, props.limit) : sorted;
});

const groupedTasks = computed(() =>
  priorityGroups
    .map(priority => ({
      priority,
      tasks: filteredTasks.value.filter(task => task.priority === priority),
    }))
    .filter(group => group.tasks.length)
);

const filterCounts = computed(() =>
  TASK_FILTERS.reduce((acc, filter) => {
    acc[filter] = scopedTasks.value.filter(task =>
      matchesFilter(task, filter)
    ).length;
    return acc;
  }, {})
);

const emptyStateTitle = computed(() => {
  if (activeFilter.value === 'today') {
    return t('TASKS.EMPTY_FILTERS.TODAY.TITLE');
  }
  if (activeFilter.value === 'overdue') {
    return t('TASKS.EMPTY_FILTERS.OVERDUE.TITLE');
  }
  if (activeFilter.value === 'upcoming') {
    return t('TASKS.EMPTY_FILTERS.UPCOMING.TITLE');
  }
  if (activeFilter.value === 'done') {
    return t('TASKS.EMPTY_FILTERS.DONE.TITLE');
  }
  return t('TASKS.EMPTY_FILTERS.ALL.TITLE');
});

const emptyStateDescription = computed(() => {
  if (activeFilter.value === 'today') {
    return t('TASKS.EMPTY_FILTERS.TODAY.DESCRIPTION');
  }
  if (activeFilter.value === 'overdue') {
    return t('TASKS.EMPTY_FILTERS.OVERDUE.DESCRIPTION');
  }
  if (activeFilter.value === 'upcoming') {
    return t('TASKS.EMPTY_FILTERS.UPCOMING.DESCRIPTION');
  }
  if (activeFilter.value === 'done') {
    return t('TASKS.EMPTY_FILTERS.DONE.DESCRIPTION');
  }
  return t('TASKS.EMPTY_FILTERS.ALL.DESCRIPTION');
});

const openCreate = (initial = {}) => {
  taskFormRef.value?.open({
    linkedContactId: props.contactId ?? initial.linkedContactId ?? null,
    linkedProjectId: props.projectId ?? initial.linkedProjectId ?? null,
    ...initial,
  });
};

const openEdit = task => {
  taskFormRef.value?.open({}, task);
};

const completeTask = task => {
  tasksStore.completeTask(task.id);
};

onMounted(() => {
  tasksStore.loadTasks();
});
</script>

<template>
  <section
    class="relative flex h-full min-h-0 w-full flex-col bg-n-solid-1"
    :class="{ 'bg-n-solid-1 lg:bg-n-solid-1': embedded }"
  >
    <div
      v-if="!embedded"
      class="sticky top-0 z-10 flex items-start justify-between bg-n-solid-1 px-5 pb-3 pt-8"
    >
      <div class="flex min-w-0 flex-col gap-2">
        <h1 class="truncate text-3xl font-bold tracking-normal text-n-slate-12">
          {{ headerTitle }}
        </h1>
        <p class="text-sm font-semibold text-n-slate-11">
          {{ todayLabel }}
        </p>
      </div>
      <Button
        v-if="activeMode === 'tasks'"
        class="shrink-0"
        slate
        faded
        sm
        icon="i-lucide-plus"
        :label="t('TASKS.CREATE_SHORT')"
        :aria-label="t('TASKS.CREATE')"
        @click="openCreate()"
      />
    </div>

    <div
      v-if="!embedded"
      class="flex min-h-11 shrink-0 gap-1 overflow-visible px-4 py-1"
    >
      <button
        type="button"
        class="inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-sm font-semibold transition-colors"
        :class="
          activeMode === 'tasks'
            ? 'bg-n-alpha-2 text-n-slate-12'
            : 'text-n-slate-10 hover:bg-n-alpha-1 hover:text-n-slate-12'
        "
        @click="activeMode = 'tasks'"
      >
        <span class="i-lucide-list-checks size-4" />
        {{ t('TASKS.AI_SUGGESTIONS.TABS.TASKS') }}
      </button>
      <button
        type="button"
        class="inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-sm font-semibold transition-colors"
        :class="
          activeMode === 'ai'
            ? 'bg-n-violet-3 text-n-violet-12 outline outline-1 outline-n-violet-6'
            : 'text-n-slate-10 hover:bg-n-violet-2 hover:text-n-violet-12'
        "
        @click="activeMode = 'ai'"
      >
        <span class="i-lucide-sparkles size-4" />
        {{ t('TASKS.AI_SUGGESTIONS.TABS.AI') }}
        <span
          class="rounded-full px-1.5 py-0.5 text-[11px] leading-none"
          :class="
            activeMode === 'ai'
              ? 'bg-n-violet-5 text-n-violet-12'
              : 'bg-n-alpha-2 text-n-slate-10'
          "
        >
          {{ AI_SUGGESTION_COUNT }}
        </span>
      </button>
    </div>

    <div
      v-if="activeMode === 'tasks'"
      class="flex gap-1 overflow-x-auto px-4 pb-2 no-scrollbar"
      :class="{ 'pt-3': embedded }"
    >
      <button
        v-for="filter in TASK_FILTERS"
        :key="filter"
        type="button"
        class="inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors"
        :class="
          activeFilter === filter
            ? 'bg-n-alpha-2 text-n-slate-12'
            : 'text-n-slate-10 hover:bg-n-alpha-1 hover:text-n-slate-12'
        "
        @click="activeFilter = filter"
      >
        {{ filterLabels[filter] }}
        <span class="text-xs opacity-70">{{ filterCounts[filter] }}</span>
      </button>
    </div>

    <TaskAISuggestions
      v-if="activeMode === 'ai' && !embedded"
      class="min-h-0 flex-1"
    />

    <div v-else class="min-h-0 flex-1 overflow-y-auto">
      <div
        v-if="state.isLoading && !state.isLoaded"
        class="flex h-full items-center justify-center py-12 text-sm text-n-slate-11"
      >
        <span class="i-lucide-loader-2 mr-2 size-4 animate-spin" />
        {{ t('TASKS.LOADING') }}
      </div>
      <div v-else-if="filteredTasks.length" class="flex flex-col">
        <template v-for="group in groupedTasks" :key="group.priority">
          <div
            class="sticky top-0 z-[1] flex items-center gap-2 bg-n-solid-1 px-4 pb-1 pt-4"
          >
            <h2 class="text-xs font-semibold tracking-normal text-n-slate-12">
              {{ priorityGroupLabel(group.priority) }}
            </h2>
            <span class="size-1 rounded-full bg-n-slate-12" />
            <span class="text-xs font-semibold text-n-slate-12">
              {{ group.tasks.length }}
            </span>
          </div>

          <template v-for="(task, index) in group.tasks" :key="task.id">
            <div v-if="index > 0" class="ml-12 mr-4 h-px bg-n-weak" />
            <TaskCard
              :task="task"
              :compact="embedded"
              @open="openEdit"
              @complete="completeTask"
            />
          </template>
        </template>
      </div>
      <TaskEmptyState
        v-else
        :title="emptyStateTitle"
        :description="emptyStateDescription"
        @create="openCreate()"
      />
    </div>

    <button
      v-if="embedded"
      type="button"
      class="flex w-full items-center justify-center gap-2 border-t border-n-weak px-4 py-3 text-sm font-medium text-n-brand hover:bg-n-alpha-1"
      @click="openCreate()"
    >
      <span class="i-lucide-plus size-4" />
      {{ t('TASKS.CREATE') }}
    </button>

    <TaskForm ref="taskFormRef" />
  </section>
</template>
